import React, { useState, useEffect, useMemo } from "react";
import "./DocumentApprovalPopup.scss";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";

interface ExtractedField {
  field: string;
  valueText?: string;
  updatedText?: string;
  extractedValue?: string; // Support legacy format
  confidence: number;
  fieldId?: number;
}

interface DocumentApprovalPopupProps {
  isOpen: boolean;
  onClose: () => void;
  documentData: {
    fileName: string;
    documentType: string;
    documentLink?: string;
    extractedFields: ExtractedField[];
    flowId: string | number;
    document_link?: string;
  } | null;
  currentConfig?: any | null;
  onApprove?: () => Promise<void>;
  onReject?: () => Promise<void>;
}

const DocumentApprovalPopup: React.FC<DocumentApprovalPopupProps> = ({
  isOpen,
  onClose,
  documentData,
  currentConfig,
  onApprove,
  onReject
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const leftPanelRef = React.useRef<HTMLDivElement>(null);
  const rightPanelRef = React.useRef<HTMLDivElement>(null);
  const [otherFields, setOtherFields] = useState<any[]>([]);
  const [otherFieldsUpdated, setOtherFieldsUpdated] = useState<any[]>([]);
  const [isViewTableModalOpen, setIsViewTableModalOpen] = useState(false);
  const [jsonDetails, setJsonDetails] = useState<any>(null);
  const [currentFieldName, setCurrentFieldName] = useState<string>("");
  const [editedJsonDetails, setEditedJsonDetails] = useState<any>(null);

  // Helper function to check if a key starts with "Score_" (case-insensitive)
  const isScoreKey = (key: string): boolean => {
    return key.toLowerCase().startsWith("score_");
  };

  // Helper function to highlight changed characters
  const highlightChangedCharacters = (original: string, updated: string) => {
    if (original === updated) return updated;

    const result: JSX.Element[] = [];
    const i = 0;
    const j = 0;
    let segmentStart = 0;
    let inDiff = false;

    // Simple character-by-character comparison
    const maxLen = Math.max(original.length, updated.length);

    for (let k = 0; k < maxLen; k++) {
      const origChar = original[k] || "";
      const updChar = updated[k] || "";

      if (origChar === updChar) {
        // Characters match
        if (inDiff) {
          // End of diff segment
          result.push(
            <span key={`diff-${segmentStart}`} className="char-highlight">
              {updated.substring(segmentStart, k)}
            </span>
          );
          inDiff = false;
          segmentStart = k;
        }
      } else {
        // Characters differ
        if (!inDiff) {
          // Start of diff segment - push normal text before this
          if (k > segmentStart) {
            result.push(
              <span key={`normal-${segmentStart}`}>
                {updated.substring(segmentStart, k)}
              </span>
            );
          }
          inDiff = true;
          segmentStart = k;
        }
      }
    }

    // Handle remaining segment
    if (segmentStart < updated.length) {
      if (inDiff) {
        result.push(
          <span key={`diff-${segmentStart}`} className="char-highlight">
            {updated.substring(segmentStart)}
          </span>
        );
      } else {
        result.push(
          <span key={`normal-${segmentStart}`}>
            {updated.substring(segmentStart)}
          </span>
        );
      }
    }

    return <>{result}</>;
  };

  // Helper function to find the score key for a given field name
  const findScoreKey = (item: any, fieldName: string): string | undefined => {
    return Object.keys(item).find(
      (key) => key.toLowerCase() === `score_${fieldName.toLowerCase()}`
    );
  };

  const handleSaveJsonDetails = () => {
    // Update the field with the edited JSON details
    // This is for viewing only in approval popup, so we don't actually save
    showNotification("Changes are view-only in approval mode", "error");
    setIsViewTableModalOpen(false);
  };

  const toggleCollapsible = (index: number) => {
    // Toggle both left and right sections with the same index
    setExpandedSections((prev) => ({
      ...prev,
      [`section_${index}`]: !prev[`section_${index}`]
    }));
  };

  // Synchronize scroll between left and right panels
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const source = e.currentTarget;
    const target =
      source === rightPanelRef.current
        ? leftPanelRef.current
        : rightPanelRef.current;

    if (target && source.scrollTop !== target.scrollTop) {
      target.scrollTop = source.scrollTop;
    }
  };

  const renderContentLeft = () => {
    return otherFields.map((item, index) => {
      try {
        const [key, value] =
          Object.entries(item).find(([k]) => !isScoreKey(k)) || [];

        if (!key) return null;

        const isJsonObject =
          typeof value === "object" && value !== null && !Array.isArray(value);
        const isArray = Array.isArray(value);

        const scoreKey = findScoreKey(item, key);
        const scoreValue = scoreKey ? item[scoreKey] : "N/A";
        const scorePercent = parseFloat(scoreValue.replace("%", ""));
        const isValidScore = !isNaN(scorePercent);

        if (isJsonObject || isArray) {
          return (
            <div key={index} className="collapsibleSection">
              <div
                className="collapsibleHeader"
                onClick={() => toggleCollapsible(index)}
              >
                <div className="collapsibleTitle">
                  <span className="field-number">{index + 1}</span>
                  <span className="statusIndicator"></span>
                  {key}
                </div>
                <div className="collapsibleHeaderRight">
                  <div className="confidenceScore">
                    {isValidScore ? (
                      <>
                        <div className="confidenceBar">
                          <div
                            className={`confidenceFill ${
                              scorePercent >= 80
                                ? "confidenceHigh"
                                : scorePercent >= 50
                                ? "confidenceMedium"
                                : "confidenceLow"
                            }`}
                            style={{ width: `${scorePercent}%` }}
                          ></div>
                        </div>
                        <span className="confidenceText">{scorePercent}%</span>
                      </>
                    ) : (
                      "N/A"
                    )}
                  </div>
                  <div className="collapsibleIcon">
                    {expandedSections[`section_${index}`] ? "▼" : "▶"}
                  </div>
                </div>
              </div>
              <div
                className={`collapsibleContent ${
                  expandedSections[`section_${index}`] ? "expanded" : ""
                }`}
              >
                <div className="complexDataContainer">
                  {isArray ? (
                    <div className="arrayData">
                      {value.length > 0 &&
                      typeof value[0] === "object" &&
                      value[0] !== null ? (
                        <div className="tableExtraction">
                          <div className="tablePreview">
                            <table>
                              <thead>
                                <tr>
                                  {Object.keys(value[0] || {})
                                    .slice(0, 4)
                                    .map((header, idx) => (
                                      <th key={idx}>{header}</th>
                                    ))}
                                  {Object.keys(value[0] || {}).length > 4 && (
                                    <th>
                                      ... +
                                      {Object.keys(value[0] || {}).length - 4}{" "}
                                      more
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {value.slice(0, 2).map((row, rowIdx) => (
                                  <tr key={rowIdx}>
                                    {Object.values(row || {})
                                      .slice(0, 4)
                                      .map((cell, cellIdx) => (
                                        <td key={cellIdx}>
                                          {typeof cell === "object"
                                            ? JSON.stringify(cell)
                                            : String(cell || "")}
                                        </td>
                                      ))}
                                    {Object.values(row || {}).length > 4 && (
                                      <td className="moreColumns">...</td>
                                    )}
                                  </tr>
                                ))}
                                {value.length > 2 && (
                                  <tr>
                                    <td
                                      colSpan={
                                        Object.keys(value[0] || {}).length > 4
                                          ? 5
                                          : 4
                                      }
                                      className="moreRows"
                                    >
                                      ... and {value.length - 2} more rows
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="arrayPreview">
                          <p>Array with {value.length} item(s)</p>
                          {value.length > 0 && (
                            <div className="arraySimplePreview">
                              {value.slice(0, 2).map((arrayItem, idx) => (
                                <div key={idx} className="arraySimpleItem">
                                  {typeof arrayItem === "object"
                                    ? JSON.stringify(arrayItem)
                                    : String(arrayItem || "")}
                                </div>
                              ))}
                              {value.length > 2 && (
                                <div className="arrayMoreItems">
                                  ... and {value.length - 2} more items
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="objectData">
                      <div className="objectProperties">
                        {Object.entries(value).map(
                          ([propKey, propValue], propIdx) => (
                            <div key={propIdx} className="propertyRow">
                              <div className="propertyKey">{propKey}:</div>
                              <div className="propertyValue">
                                {typeof propValue === "object" &&
                                propValue !== null
                                  ? JSON.stringify(propValue, null, 2)
                                  : String(propValue || "N/A")}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  <div className="viewDetailsContainer">
                    <button
                      className="viewDetailsBtn"
                      onClick={(e) => {
                        e.preventDefault();
                        setJsonDetails(value);
                        setEditedJsonDetails(JSON.parse(JSON.stringify(value)));
                        setCurrentFieldName(key);
                        setIsViewTableModalOpen(true);
                      }}
                    >
                      View Full Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Render simple fields (string, number, date)
        const stringValue = String(value || "");
        const isLongText = typeof value === "string" && value.length > 100;

        return (
          <div key={index} className="extractionRow">
            <div className="fieldNumber">{index + 1}</div>
            <div className="fieldLabel">{key}</div>
            {isLongText ? (
              <textarea
                className="fieldValue"
                placeholder="Not detected"
                style={
                  stringValue === ""
                    ? { fontStyle: "italic", color: "#999" }
                    : {
                        color: "#333333",
                        minHeight: "100px",
                        lineHeight: "1.5"
                      }
                }
                value={stringValue}
                readOnly
                rows={4}
              />
            ) : (
              <input
                type="text"
                className="fieldValue"
                placeholder="Not detected"
                style={
                  stringValue === ""
                    ? { fontStyle: "italic", color: "#999" }
                    : { color: "#333333" }
                }
                value={stringValue}
                readOnly
              />
            )}
            <div className="confidenceScore">
              {isValidScore ? (
                <>
                  <div className="confidenceBar">
                    <div
                      className={`confidenceFill ${
                        scorePercent >= 80
                          ? "confidenceHigh"
                          : scorePercent >= 50
                          ? "confidenceMedium"
                          : "confidenceLow"
                      }`}
                      style={{ width: `${scorePercent}%` }}
                    ></div>
                  </div>
                  <span className="confidenceText">{scorePercent}%</span>
                </>
              ) : (
                "N/A"
              )}
            </div>
          </div>
        );
      } catch (error) {
        console.error("Error rendering field:", error);
        return null;
      }
    });
  };

  const renderContentRight = () => {
    return otherFieldsUpdated.map((item, index) => {
      try {
        const [key, value] =
          Object.entries(item).find(([k]) => !isScoreKey(k)) || [];

        if (!key) return null;

        const isJsonObject =
          typeof value === "object" && value !== null && !Array.isArray(value);
        const isArray = Array.isArray(value);

        const scoreKey = findScoreKey(item, key);
        const scoreValue = scoreKey ? item[scoreKey] : "N/A";
        const scorePercent = parseFloat(scoreValue.replace("%", ""));
        const isValidScore = !isNaN(scorePercent);

        // Check if this field has changed
        const originalValue = otherFields[index]?.[key];
        const hasChangedValue =
          JSON.stringify(value) !== JSON.stringify(originalValue);

        if (isJsonObject || isArray) {
          return (
            <div
              key={index}
              className={`collapsibleSection ${
                hasChangedValue ? "has-changes" : ""
              }`}
            >
              <div
                className="collapsibleHeader"
                onClick={() => toggleCollapsible(index)}
              >
                <div className="collapsibleTitle">
                  <span className="field-number">{index + 1}</span>
                  <span className="statusIndicator"></span>
                  {key}
                </div>
                <div className="collapsibleHeaderRight">
                  <div className="confidenceScore">
                    {isValidScore ? (
                      <>
                        <div className="confidenceBar">
                          <div
                            className={`confidenceFill ${
                              scorePercent >= 80
                                ? "confidenceHigh"
                                : scorePercent >= 50
                                ? "confidenceMedium"
                                : "confidenceLow"
                            }`}
                            style={{ width: `${scorePercent}%` }}
                          ></div>
                        </div>
                        <span className="confidenceText">{scorePercent}%</span>
                      </>
                    ) : (
                      "N/A"
                    )}
                  </div>
                  <div className="collapsibleIcon">
                    {expandedSections[`section_${index}`] ? "▼" : "▶"}
                  </div>
                </div>
              </div>
              <div
                className={`collapsibleContent ${
                  expandedSections[`section_${index}`] ? "expanded" : ""
                }`}
              >
                <div className="complexDataContainer">
                  {isArray ? (
                    <div className="arrayData">
                      {value.length > 0 &&
                      typeof value[0] === "object" &&
                      value[0] !== null ? (
                        <div className="tableExtraction">
                          <div className="tablePreview">
                            <table>
                              <thead>
                                <tr>
                                  {Object.keys(value[0] || {})
                                    .slice(0, 4)
                                    .map((header, idx) => (
                                      <th key={idx}>{header}</th>
                                    ))}
                                  {Object.keys(value[0] || {}).length > 4 && (
                                    <th>
                                      ... +
                                      {Object.keys(value[0] || {}).length - 4}{" "}
                                      more
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {value.slice(0, 2).map((row, rowIdx) => {
                                  const originalRow = Array.isArray(
                                    originalValue
                                  )
                                    ? originalValue[rowIdx]
                                    : null;
                                  return (
                                    <tr key={rowIdx}>
                                      {Object.entries(row || {})
                                        .slice(0, 4)
                                        .map(([cellKey, cell], cellIdx) => {
                                          const originalCell =
                                            originalRow?.[cellKey];
                                          const cellChanged =
                                            JSON.stringify(originalCell) !==
                                            JSON.stringify(cell);
                                          return (
                                            <td
                                              key={cellIdx}
                                              className={
                                                cellChanged
                                                  ? "cell-changed"
                                                  : ""
                                              }
                                            >
                                              {cellChanged &&
                                              typeof cell !== "object" &&
                                              typeof originalCell !== "object"
                                                ? highlightChangedCharacters(
                                                    String(originalCell || ""),
                                                    String(cell || "")
                                                  )
                                                : typeof cell === "object"
                                                ? JSON.stringify(cell)
                                                : String(cell || "")}
                                            </td>
                                          );
                                        })}
                                      {Object.values(row || {}).length > 4 && (
                                        <td className="moreColumns">...</td>
                                      )}
                                    </tr>
                                  );
                                })}
                                {value.length > 2 && (
                                  <tr>
                                    <td
                                      colSpan={
                                        Object.keys(value[0] || {}).length > 4
                                          ? 5
                                          : 4
                                      }
                                      className="moreRows"
                                    >
                                      ... and {value.length - 2} more rows
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="arrayPreview">
                          <p>Array with {value.length} item(s)</p>
                          {value.length > 0 && (
                            <div className="arraySimplePreview">
                              {value.slice(0, 2).map((arrayItem, idx) => (
                                <div key={idx} className="arraySimpleItem">
                                  {typeof arrayItem === "object"
                                    ? JSON.stringify(arrayItem)
                                    : String(arrayItem || "")}
                                </div>
                              ))}
                              {value.length > 2 && (
                                <div className="arrayMoreItems">
                                  ... and {value.length - 2} more items
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="objectData">
                      <div className="objectProperties">
                        {Object.entries(value).map(
                          ([propKey, propValue], propIdx) => {
                            // Check if this specific property has changed
                            const originalObj = otherFields[index]?.[key];
                            const isPropChanged =
                              originalObj &&
                              JSON.stringify(originalObj[propKey]) !==
                                JSON.stringify(propValue);

                            return (
                              <div key={propIdx} className="propertyRow">
                                <div className="propertyKey">{propKey}:</div>
                                <div
                                  className={`propertyValue ${
                                    isPropChanged ? "changed-property" : ""
                                  }`}
                                >
                                  {isPropChanged
                                    ? typeof propValue === "object" &&
                                      propValue !== null
                                      ? JSON.stringify(propValue, null, 2)
                                      : highlightChangedCharacters(
                                          String(originalObj?.[propKey] || ""),
                                          String(propValue || "N/A")
                                        )
                                    : typeof propValue === "object" &&
                                      propValue !== null
                                    ? JSON.stringify(propValue, null, 2)
                                    : String(propValue || "N/A")}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}

                  <div className="viewDetailsContainer">
                    <button
                      className="viewDetailsBtn"
                      onClick={(e) => {
                        e.preventDefault();
                        setJsonDetails(value);
                        setEditedJsonDetails(JSON.parse(JSON.stringify(value)));
                        setCurrentFieldName(key);
                        setIsViewTableModalOpen(true);
                      }}
                    >
                      View Full Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Render simple fields (string, number, date)
        const stringValue = String(value || "");
        const originalStringValue = String(originalValue || "");
        const isLongText = typeof value === "string" && value.length > 100;

        return (
          <div
            key={index}
            className={`extractionRow ${hasChangedValue ? "has-changes" : ""}`}
          >
            <div className="fieldNumber">{index + 1}</div>
            <div className="fieldLabel">{key}</div>
            {isLongText ? (
              <textarea
                className="fieldValue"
                value={stringValue || "Not detected"}
                readOnly
                rows={4}
                style={
                  stringValue === ""
                    ? { fontStyle: "italic", color: "#999", minHeight: "100px" }
                    : {
                        color: "#333333",
                        minHeight: "100px"
                      }
                }
              />
            ) : (
              <div
                className="fieldValue input-like"
                style={
                  stringValue === ""
                    ? { fontStyle: "italic", color: "#999" }
                    : { color: "#333333" }
                }
              >
                {hasChangedValue && stringValue !== ""
                  ? highlightChangedCharacters(originalStringValue, stringValue)
                  : stringValue || "Not detected"}
              </div>
            )}
            <div className="confidenceScore">
              {isValidScore ? (
                <>
                  <div className="confidenceBar">
                    <div
                      className={`confidenceFill ${
                        scorePercent >= 80
                          ? "confidenceHigh"
                          : scorePercent >= 50
                          ? "confidenceMedium"
                          : "confidenceLow"
                      }`}
                      style={{ width: `${scorePercent}%` }}
                    ></div>
                  </div>
                  <span className="confidenceText">{scorePercent}%</span>
                </>
              ) : (
                "N/A"
              )}
            </div>
          </div>
        );
      } catch (error) {
        console.error("Error rendering field:", error);
        return null;
      }
    });
  };

  // Helper function to parse Python-style dict/list strings to JSON
  const parsePythonStyleJSON = (text: string): any => {
    try {
      return JSON.parse(text);
    } catch {
      try {
        const normalized = text
          .replace(/{/g, "{")
          .replace(/}/g, "}")
          .replace(/\[/g, "[")
          .replace(/\]/g, "]")
          // Replace Python None with JSON null
          .replace(/:\s*None\s*([,}])/g, ": null$1")
          .replace(/,\s*None\s*([,}])/g, ", null$1")
          .replace(/\[\s*None\s*([,]])/g, "[null$1")
          // Replace Python True/False with JSON true/false
          .replace(/:\s*True\s*([,}])/g, ": true$1")
          .replace(/:\s*False\s*([,}])/g, ": false$1")
          .replace(/,\s*True\s*([,}])/g, ", true$1")
          .replace(/,\s*False\s*([,}])/g, ", false$1")
          .replace(/'([^']*)':/g, '"$1":')
          .replace(/:\s*'([^']*)'/g, ': "$1"')
          .replace(/,\s*'([^']*)':/g, ', "$1":')
          .replace(/\[\s*'([^']*)'/g, '["$1"')
          .replace(/,\s*'([^']*)'/g, ', "$1"')
          .replace(/'([^']*)'(?=\s*[,}]])/g, '"$1"');

        return JSON.parse(normalized);
      } catch {
        return text;
      }
    }
  };

  // Check if value has changed
  const hasChanged = (valueText: string, updatedText: string): boolean => {
    if (!updatedText || updatedText.trim() === "") return false;
    return valueText !== updatedText;
  };

  const filteredFields = useMemo((): ExtractedField[] => {
    if (
      currentConfig?.extracteddata &&
      Array.isArray(currentConfig.extracteddata)
    ) {
      const extractedFields = currentConfig.extracteddata
        .filter(
          (field: any) => field.field_name.toLowerCase() !== "total token cost"
        )
        .map((field: any) => {
          let valueText: any = "";
          let updatedText: any = "";

          // Get value_text
          if (field.value_text !== null && field.value_text !== "") {
            const parsed = parsePythonStyleJSON(field.value_text);
            valueText =
              typeof parsed === "object" && parsed !== field.value_text
                ? parsed
                : field.value_text;
          } else if (field.value_number !== null) {
            valueText = String(field.value_number);
          } else if (field.value_date !== null) {
            valueText = field.value_date;
          }

          // Get updated_text
          if (
            field.updated_text !== null &&
            field.updated_text !== "" &&
            field.updated_text.trim() !== ""
          ) {
            const parsed = parsePythonStyleJSON(field.updated_text);
            updatedText =
              typeof parsed === "object" && parsed !== field.updated_text
                ? parsed
                : field.updated_text;
          }

          return {
            field: field.field_name,
            valueText: valueText || "",
            updatedText: updatedText || valueText || "",
            confidence: field.confidence_score || 0,
            fieldId: field.field_id
          };
        });

      // Convert to otherFields format for left side (value_text)
      const convertedValueText = extractedFields.map((field: any) => ({
        [field.field]: field.valueText,
        [`Score_${field.field}`]: `${field.confidence}%`
      }));
      setOtherFields(convertedValueText);

      // Convert to otherFieldsUpdated format for right side (updated_text)
      const convertedUpdatedText = extractedFields.map((field: any) => ({
        [field.field]: field.updatedText,
        [`Score_${field.field}`]: `${field.confidence}%`
      }));
      setOtherFieldsUpdated(convertedUpdatedText);

      return extractedFields;
    } else {
      const fields =
        documentData?.extractedFields?.filter(
          (field) => field.field.toLowerCase() !== "total token cost"
        ) || [];

      // Map legacy format (extractedValue) to new format (valueText/updatedText)
      const mappedFields = fields.map((field) => ({
        field: field.field,
        valueText: field.valueText || field.extractedValue || "",
        updatedText: field.updatedText || field.extractedValue || "",
        confidence: field.confidence || 0,
        fieldId: field.fieldId
      }));

      // Convert to otherFields format for legacy data
      const convertedValueText = mappedFields.map((field) => ({
        [field.field]: field.valueText,
        [`Score_${field.field}`]: `${field.confidence}%`
      }));
      setOtherFields(convertedValueText);

      const convertedUpdatedText = mappedFields.map((field) => ({
        [field.field]: field.updatedText,
        [`Score_${field.field}`]: `${field.confidence}%`
      }));
      setOtherFieldsUpdated(convertedUpdatedText);

      return mappedFields;
    }
  }, [currentConfig?.extracteddata, documentData?.extractedFields]);

  const showNotification = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    const variant =
      type === "error" ? ToastVariant.ERROR : ToastVariant.SUCCESS;
    ToastMessageUtil.show(message, variant);
  };

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      if (onApprove) {
        await onApprove();
        showNotification("Document approved successfully");
        onClose();
      }
    } catch (error) {
      showNotification("Failed to approve document", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      if (onReject) {
        await onReject();
        showNotification("Document rejected successfully");
        onClose();
      }
    } catch (error) {
      showNotification("Failed to reject document", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const renderFieldValue = (value: any) => {
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        return (
          <div className="complex-value">
            <span className="value-type">Array ({value.length} items)</span>
            <pre className="value-preview">
              {JSON.stringify(value, null, 2)}
            </pre>
          </div>
        );
      } else {
        return (
          <div className="complex-value">
            <span className="value-type">Object</span>
            <pre className="value-preview">
              {JSON.stringify(value, null, 2)}
            </pre>
          </div>
        );
      }
    }
    return <span className="simple-value">{String(value || "")}</span>;
  };

  if (!isOpen || !documentData) return null;

  return (
    <div className="document-approval-overlay">
      <div className="overlay-background" onClick={onClose}>
        <div className="document-approval-modal" onClick={handleModalClick}>
          {/* Header */}
          <div className="modal-header">
            <div className="header-content">
              <div className="breadcrumb">
                <h2 className="breadcrumb-item">Document Approval</h2>
              </div>
              <div className="header-actions">
                {documentData?.documentLink && (
                  <a
                    href={documentData.documentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="document-link-button"
                    title="View Document"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M14 2H2c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zM2 13V3h12v10H2z" />
                      <path d="M4 5h8v1H4zm0 2h8v1H4zm0 2h5v1H4z" />
                    </svg>
                    View Document
                  </a>
                )}
                <button className="close-button" onClick={onClose}>
                  ×
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="modal-content">
            <div className="content-layout">
              {/* Left side - Original Values (value_text) */}
              <div className="values-panel">
                <div className="panel-header">
                  <h2>Original Extracted Values</h2>
                  <p className="panel-subtitle">
                    {documentData.fileName} - {documentData.documentType}
                  </p>
                </div>

                <div className="extraction-fields" ref={leftPanelRef}>
                  <div className="extraction-table">
                    <div className="table-header">
                      <div className="header-cell">#</div>
                      <div className="header-cell">Field</div>
                      <div className="header-cell">Extracted value</div>
                      <div className="header-cell">Confidence</div>
                    </div>
                    {renderContentLeft()}
                  </div>
                </div>
              </div>

              {/* Right side - Updated Values (updated_text) */}
              <div className="values-panel updated-panel">
                <div className="panel-header">
                  <h2>Updated Values</h2>
                  <p className="panel-subtitle">
                    Review changes before approval
                  </p>
                </div>

                <div
                  className="extraction-fields"
                  ref={rightPanelRef}
                  onScroll={handleScroll}
                >
                  <div className="extraction-table">
                    <div className="table-header">
                      <div className="header-cell">#</div>
                      <div className="header-cell">Field</div>
                      <div className="header-cell">Extracted value</div>
                      <div className="header-cell">Confidence</div>
                    </div>
                    {renderContentRight()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with action buttons */}
          <div className="modal-footer">
            <div className="footer-info">
              <span className="info-text">
                Review the changes and approve or reject the document
              </span>
            </div>
            <div className="action-buttons">
              <button
                className="btn-secondary"
                onClick={onClose}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleReject}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Reject"}
              </button>
              <button
                className="btn-success"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Table Modal */}
      {isViewTableModalOpen && (
        <div
          className="view-table-modal-overlay"
          onClick={() => setIsViewTableModalOpen(false)}
        >
          <div
            className="view-table-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="view-table-header">
              <h3>
                {currentFieldName}{" "}
                {Array.isArray(jsonDetails)
                  ? `Details (${jsonDetails.length} items)`
                  : "Details"}
              </h3>
              <div className="modal-actions">
                <button className="save-button" onClick={handleSaveJsonDetails}>
                  Close
                </button>
                <button
                  className="close-button"
                  onClick={() => setIsViewTableModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="view-table-content">
              {Array.isArray(jsonDetails) ? (
                <div className="array-detail-view">
                  {jsonDetails.length > 0 &&
                  typeof jsonDetails[0] === "object" ? (
                    <div className="table-view-container">
                      <table className="editable-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {Object.keys(jsonDetails[0] || {}).map(
                              (header, idx) => (
                                <th key={idx}>{header}</th>
                              )
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {jsonDetails.map((item, index) => (
                            <tr key={index}>
                              <td className="row-number">{index + 1}</td>
                              {Object.entries(item).map(
                                ([key, value], cellIdx) => (
                                  <td key={cellIdx}>
                                    {typeof value === "object" && value !== null
                                      ? JSON.stringify(value, null, 2)
                                      : String(value || "")}
                                  </td>
                                )
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    jsonDetails.map((item, index) => (
                      <div key={index} className="array-item">
                        <div className="array-item-header">
                          Item #{index + 1}
                        </div>
                        <div className="array-item-content">
                          {item !== undefined && item !== null
                            ? String(item)
                            : ""}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : typeof jsonDetails === "object" && jsonDetails !== null ? (
                <div className="object-detail-view">
                  <div className="object-properties-detailed">
                    {Object.entries(jsonDetails).map(([key, value]) => (
                      <div key={key} className="property-row-detailed">
                        <div className="property-key-detailed">{key}:</div>
                        <div className="property-value-detailed">
                          {typeof value === "object" && value !== null
                            ? JSON.stringify(value, null, 2)
                            : String(value || "")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <pre className="json-fallback">
                  {JSON.stringify(jsonDetails, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentApprovalPopup;
